// ScoreGraph.js - Premium CELPIP Speaking Score Assessment & Breakdown
import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register chart elements
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const ScoreGraph = ({ scores }) => {
    const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'chart'

    if (!scores) {
        return (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
                <p>No score data available.</p>
            </div>
        );
    }

    const overall = Math.round(scores.overallScore || 0);
    const taskFulfillment = Math.round(scores.taskFulfillmentScore || 0);
    const coherence = Math.round(scores.coherenceScore || 0);
    const vocabulary = Math.round(scores.vocabularyScore || 0);
    const grammar = Math.round(scores.grammarScore || 0);

    // Determine CLB band descriptor and color theme
    const getClbBadge = (score) => {
        if (score >= 10) return { label: 'Advanced / Native-like', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' };
        if (score >= 8) return { label: 'Effective / Fluent', color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd' };
        if (score >= 6) return { label: 'Adequate / Intermediate', color: '#d97706', bg: '#fffbeb', border: '#fde68a' };
        if (score >= 4) return { label: 'Basic / Developing', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' };
        return { label: 'Minimal', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' };
    };

    const overallBadge = getClbBadge(overall);

    const criteriaList = [
        {
            key: 'fulfillment',
            title: 'Task Fulfillment (Content)',
            icon: '🎯',
            score: taskFulfillment,
            justification: scores.taskFulfillmentJustification || 'Addresses the required prompt points with appropriate context.',
            color: '#0284c7'
        },
        {
            key: 'coherence',
            title: 'Coherence & Organization',
            icon: '🔄',
            score: coherence,
            justification: scores.coherenceJustification || 'Organized sequence of ideas with connective transition phrases.',
            color: '#8b5cf6'
        },
        {
            key: 'vocabulary',
            title: 'Vocabulary (Lexical Resource)',
            icon: '📚',
            score: vocabulary,
            justification: scores.vocabularyJustification || 'Appropriate lexical range with context-relevant phrasing.',
            color: '#10b981'
        },
        {
            key: 'grammar',
            title: 'Grammar & Sentence Structure',
            icon: '✍️',
            score: grammar,
            justification: scores.grammarJustification || 'Demonstrates understandable sentence structure and grammar accuracy.',
            color: '#f59e0b'
        }
    ];

    // Chart.js configuration for the Chart View
    const chartData = {
        labels: ['Task Fulfillment', 'Coherence', 'Vocabulary', 'Grammar', 'Overall CLB'],
        datasets: [
            {
                label: 'CLB Score',
                data: [taskFulfillment, coherence, vocabulary, grammar, overall],
                backgroundColor: [
                    'rgba(2, 132, 199, 0.85)',
                    'rgba(139, 92, 246, 0.85)',
                    'rgba(16, 185, 129, 0.85)',
                    'rgba(245, 158, 11, 0.85)',
                    'rgba(15, 23, 42, 0.92)'
                ],
                borderColor: [
                    '#0284c7',
                    '#8b5cf6',
                    '#10b981',
                    '#f59e0b',
                    '#0f172a'
                ],
                borderWidth: 1.5,
                borderRadius: 6,
                barThickness: 28,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#1e293b',
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 },
                padding: 10,
                callbacks: {
                    label: (context) => ` Score: ${context.raw} / 12`
                }
            },
            datalabels: {
                color: '#ffffff',
                anchor: 'center',
                align: 'center',
                font: { weight: 'bold', size: 13 },
                formatter: (val) => `${val}/12`
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                max: 12,
                ticks: {
                    stepSize: 2,
                    color: '#64748b',
                    font: { size: 12, weight: '600' }
                },
                grid: { color: '#f1f5f9' }
            },
            y: {
                ticks: {
                    color: '#1e293b',
                    font: { size: 13.5, weight: '700' }
                },
                grid: { display: false }
            }
        }
    };

    const strengths = scores.strengths && Array.isArray(scores.strengths) && scores.strengths.length > 0
        ? scores.strengths
        : [
            'Directly answered the main prompt requirements',
            'Maintained clear communication and intelligible speech'
        ];

    const improvements = scores.improvements && Array.isArray(scores.improvements) && scores.improvements.length > 0
        ? scores.improvements
        : [
            'Incorporate varied connective words (e.g. "Furthermore", "Consequently")',
            'Expand vocabulary by using descriptive synonyms and idioms'
        ];

    return (
        <div className="celpip-score-modal-container" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {/* Header with Title and Mode Switcher */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ margin: '0 0 4px 0', fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                        Speaking Evaluation & Score
                    </h2>
                    <p style={{ margin: 0, fontSize: 13.5, color: '#64748b' }}>
                        Evaluated against official Canadian Language Benchmark (CLB) criteria
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 6, backgroundColor: '#f1f5f9', padding: 4, borderRadius: 8 }}>
                    <button
                        type="button"
                        onClick={() => setViewMode('detailed')}
                        style={{
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: 'none',
                            backgroundColor: viewMode === 'detailed' ? '#ffffff' : 'transparent',
                            color: viewMode === 'detailed' ? '#0f172a' : '#64748b',
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: 'pointer',
                            boxShadow: viewMode === 'detailed' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        Detailed Breakdown
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('chart')}
                        style={{
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: 'none',
                            backgroundColor: viewMode === 'chart' ? '#ffffff' : 'transparent',
                            color: viewMode === 'chart' ? '#0f172a' : '#64748b',
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: 'pointer',
                            boxShadow: viewMode === 'chart' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        Performance Chart
                    </button>
                </div>
            </div>

            {/* Overall Score Hero Card */}
            <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: 12,
                padding: '22px 28px',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 24,
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.15)',
                flexWrap: 'wrap',
                gap: 16
            }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{
                            backgroundColor: overallBadge.bg,
                            color: overallBadge.color,
                            border: `1px solid ${overallBadge.border}`,
                            padding: '3px 10px',
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            {overallBadge.label}
                        </span>
                        <span style={{ fontSize: 13, color: '#94a3b8' }}>
                            {scores.clbLevel || `CLB Level ${overall}`}
                        </span>
                    </div>
                    <div style={{ fontSize: 14.5, color: '#e2e8f0', lineHeight: 1.55 }}>
                        {scores.overallJustification || 'Good communicative proficiency demonstrated across the task.'}
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: '1.5px solid rgba(255, 255, 255, 0.18)',
                    borderRadius: 12,
                    padding: '12px 26px',
                    minWidth: 120
                }}>
                    <span style={{ fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: 700 }}>
                        Overall Score
                    </span>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                        <span style={{ fontSize: 38, fontWeight: 900, color: '#38bdf8', lineHeight: 1 }}>
                            {overall}
                        </span>
                        <span style={{ fontSize: 18, fontWeight: 700, color: '#94a3b8' }}>/12</span>
                    </div>
                </div>
            </div>

            {/* View Mode: Detailed Breakdown */}
            {viewMode === 'detailed' ? (
                <>
                    {/* 4 Dimension Cards Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
                        gap: 16,
                        marginBottom: 24
                    }}>
                        {criteriaList.map((crit) => {
                            const pct = Math.round((crit.score / 12) * 100);
                            return (
                                <div
                                    key={crit.key}
                                    style={{
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 10,
                                        padding: '16px 18px',
                                        backgroundColor: '#ffffff',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ fontSize: 18 }}>{crit.icon}</span>
                                            <span style={{ fontSize: 14.5, fontWeight: 700, color: '#1e293b' }}>
                                                {crit.title}
                                            </span>
                                        </div>
                                        <span style={{
                                            fontSize: 15,
                                            fontWeight: 800,
                                            color: crit.color,
                                            backgroundColor: `${crit.color}15`,
                                            padding: '2px 8px',
                                            borderRadius: 6
                                        }}>
                                            {crit.score} / 12
                                        </span>
                                    </div>

                                    {/* Progress Bar */}
                                    <div style={{
                                        width: '100%',
                                        height: 7,
                                        backgroundColor: '#e2e8f0',
                                        borderRadius: 10,
                                        overflow: 'hidden',
                                        marginBottom: 10
                                    }}>
                                        <div style={{
                                            width: `${pct}%`,
                                            height: '100%',
                                            backgroundColor: crit.color,
                                            borderRadius: 10,
                                            transition: 'width 0.6s ease'
                                        }} />
                                    </div>

                                    <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                                        {crit.justification}
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Strengths & Improvements */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                        gap: 16
                    }}>
                        {/* Strengths */}
                        <div style={{
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: 10,
                            padding: '16px 20px'
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#15803d', fontSize: 14.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>🌟</span> What You Did Well
                            </h4>
                            <ul style={{ margin: 0, paddingLeft: 18, color: '#166534', fontSize: 13, lineHeight: 1.6 }}>
                                {strengths.map((item, idx) => (
                                    <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
                                ))}
                            </ul>
                        </div>

                        {/* Improvements */}
                        <div style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 10,
                            padding: '16px 20px'
                        }}>
                            <h4 style={{ margin: '0 0 10px 0', color: '#0369a1', fontSize: 14.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>💡</span> Tips to Boost Your Score
                            </h4>
                            <ul style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 13, lineHeight: 1.6 }}>
                                {improvements.map((item, idx) => (
                                    <li key={idx} style={{ marginBottom: 4 }}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </>
            ) : (
                /* View Mode: Performance Chart */
                <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '24px 20px',
                    height: 380
                }}>
                    <Bar data={chartData} options={chartOptions} />
                </div>
            )}
        </div>
    );
};

export default ScoreGraph;
